/**
 * Wynajem krótkoterminowy (minuty / godziny / dni) — kontrakt dla frontu §1, §2, §8.
 *
 * Wszystkie daty z godziną w tym trybie ("RRRR-MM-DD GG:MM") są czasem ŚCIENNYM w strefie serwera/placu
 * (SERVER_TIMEZONE), więc liczymy je jako "naiwne" minuty (Date.UTC, bez strefy urządzenia) — dzięki temu
 * klient w innej strefie widzi te same godziny co plac, a porównania z calendar.busy[] są spójne.
 */

export const SERVER_TIMEZONE = 'Europe/Warsaw'

export type ShortTermUnit = 'min' | 'h' | 'd' | 'm'

export interface ShortTermTier {
  tier: string
  unit: ShortTermUnit
  threshold: number
  minutes: number
  rate: number
  rateGross?: number
  rateNet?: number
  ratePer?: string
}

export interface ShortTermConfig {
  unitMinutes: number
  minMinutes: number
  maxMinutes: number | null
  serviceBufferMinutes?: number
  pricelistId?: string
  pricingMode?: string
  leadingPrice?: 'gross' | 'net'
  higherUnitCap?: boolean
  vat?: number
  /** false = cennik bez progów min/h — nie pokazujemy wyboru godzin */
  capable?: boolean
  grid?: ShortTermTier[]
}

export interface BusyWindow {
  from: string
  to: string
}

export interface ShortTermCalendarData {
  start?: string | null
  busyRange?: BusyWindow
  busy?: BusyWindow[]
}

export interface RentalWindowPricing {
  unit: ShortTermUnit
  quantity: number
  unitRateGross: number
  unitRateNet: number
  gross: number
  net: number
}

export interface RentalWindow {
  start: string
  end: string
  minutes: number
  timezone: string
  notice?: string
  pricing?: RentalWindowPricing
}

export type WindowUnavailableReason = 'renting_blocked' | 'service' | 'service_buffer' | 'indefinite_contract' | 'contract_period' | 'other_reservation'

export interface WindowAvailability {
  available: boolean
  reason?: WindowUnavailableReason
  nextAvailableFrom?: string | null
  busy?: BusyWindow[]
}

/** błędy pozycji koszyka (validationErrors[].type), które klient naprawia zmianą okna najmu (§2.5); "warehouse" idzie starą ścieżką */
export const WINDOW_ERROR_TYPES = ['date', 'window_unavailable', 'pricelist']

export interface WindowError {
  types: string[]
  errors: string[]
  availability?: WindowAvailability
}

export const UNIT_MINUTES: Record<ShortTermUnit, number> = { min: 1, h: 60, d: 1440, m: 43200 }
export const DAY_MINUTES = 1440

/** czy obiekt z checkPrice jest w trybie krótkoterminowym (blok shortTerm i cennik obsługujący godziny) */
export const isShortTermCapable = (shortTerm?: ShortTermConfig | null): shortTerm is ShortTermConfig =>
  !!shortTerm && typeof shortTerm === 'object' && shortTerm.capable !== false && Number(shortTerm.unitMinutes) > 0

// ---------------------------------------------------------------------------------------------
// czas ścienny

const pad = (n: number) => String(n).padStart(2, '0')

/** "2026-10-05 10:00" | "2026-10-05T10:00:00" | "2026-10-05" -> minuty (naiwne, czas ścienny) */
export const parseWall = (value?: string | null): number | undefined => {
  if (!value || typeof value !== 'string') return
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/)
  if (!m) return
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0)) / 60000
}

/** minuty -> "RRRR-MM-DD GG:MM" */
export const formatWall = (minutes: number) => {
  const d = new Date(minutes * 60000)
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

export const wallDay = (minutes: number) => Math.floor(minutes / DAY_MINUTES) * DAY_MINUTES
export const wallTime = (minutes: number) => formatWall(minutes).slice(11, 16)

/** "teraz" jako czas ścienny w strefie placu */
export const nowWall = (timeZone = SERVER_TIMEZONE) => {
  const now = new Date()
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(now)
    const get = (type: string) => Number(parts.find(p => p.type === type)?.value)
    return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute')) / 60000
  } catch {
    return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes()) / 60000
  }
}

export const roundUp = (value: number, step: number) => step > 0 ? Math.ceil(value / step) * step : value
export const roundDown = (value: number, step: number) => step > 0 ? Math.floor(value / step) * step : value

/** krok selektora godzin startu: wielokrotność unitMinutes, nie gęściej niż co 30 min */
export const getSlotStep = (shortTerm: ShortTermConfig) => {
  const unit = Math.max(1, Number(shortTerm.unitMinutes) || 30)
  return unit * Math.max(1, Math.ceil(30 / unit))
}

// ---------------------------------------------------------------------------------------------
// zajętość

export interface BusyInterval {
  from: number
  to: number
}

export const parseBusy = (busy?: BusyWindow[]): BusyInterval[] => (Array.isArray(busy) ? busy : [])
  .map(b => ({ from: parseWall(b?.from), to: parseWall(b?.to) }))
  .filter((b): b is BusyInterval => b.from !== undefined && b.to !== undefined && b.to > b.from)
  .sort((a, b) => a.from - b.from)

/** najem zajmuje [start, end) — koniec okna busy jest pierwszą wolną minutą */
export const isWindowFree = (busy: BusyInterval[], start: number, end: number) =>
  !busy.some(b => start < b.to && end > b.from)

/** do kiedy (wyłącznie) obiekt jest wolny od startu; Infinity = do końca horyzontu */
export const freeUntil = (busy: BusyInterval[], start: number) => {
  if (busy.some(b => start >= b.from && start < b.to)) return start
  const next = busy.find(b => b.from >= start)
  return next ? next.from : Infinity
}

/** horyzont kalendarza (od teraz do busyRange.to) */
export const getHorizon = (calendar?: ShortTermCalendarData, now = nowWall()) => {
  const availableFrom = parseWall(calendar?.start)
  const from = Math.max(now, availableFrom ?? now)
  const to = parseWall(calendar?.busyRange?.to) ?? (now + 60 * DAY_MINUTES)
  return { from, to }
}

export const findFirstFreeStart = (busy: BusyInterval[], from: number, until: number, duration: number, step: number) => {
  for (let s = roundUp(from, step); s < until; s += step) {
    if (isWindowFree(busy, s, s + duration)) return s
  }
}

// ---------------------------------------------------------------------------------------------
// długości i ceny

/** proponowane długości najmu: minimum polityki + progi cennika (bez progów miesięcznych — to tryb miesięczny) */
export const getDurationOptions = (shortTerm: ShortTermConfig) => {
  const min = Math.max(1, Number(shortTerm.minMinutes) || Number(shortTerm.unitMinutes) || 30)
  const max = shortTerm.maxMinutes ? Number(shortTerm.maxMinutes) : Infinity
  const values = new Set<number>([roundUp(min, shortTerm.unitMinutes)])
  for (const tier of shortTerm.grid || []) {
    if (tier.unit === 'm') continue
    const minutes = Number(tier.minutes) || (UNIT_MINUTES[tier.unit] || 0) * Number(tier.threshold || 1)
    if (minutes >= min && minutes <= max) values.add(roundUp(minutes, shortTerm.unitMinutes))
  }
  return Array.from(values).filter(v => v <= max).sort((a, b) => a - b)
}

/** domyślna długość: najkrótszy próg cennika ≥ 1 h (np. 1 h zamiast 30 min), inaczej minimum */
export const getDefaultDuration = (shortTerm: ShortTermConfig) => {
  const options = getDurationOptions(shortTerm)
  return options.find(o => o >= 60) || options[0] || Number(shortTerm.minMinutes) || 60
}

/** domyślne okno: najbliższy wolny start od teraz + domyślna długość */
export const getDefaultWindow = (shortTerm: ShortTermConfig, calendar?: ShortTermCalendarData) => {
  const busy = parseBusy(calendar?.busy)
  const horizon = getHorizon(calendar)
  const durationMinutes = getDefaultDuration(shortTerm)
  const step = getSlotStep(shortTerm)
  const start = findFirstFreeStart(busy, horizon.from, horizon.to, durationMinutes, step)
    ?? findFirstFreeStart(busy, horizon.from, horizon.to, Number(shortTerm.minMinutes) || step, step)
  if (start === undefined) return
  const fitting = Math.min(durationMinutes, roundDown(freeUntil(busy, start) - start, shortTerm.unitMinutes))
  return {
    startDateTime: formatWall(start),
    durationMinutes: Math.max(fitting, Number(shortTerm.minMinutes) || 0)
  }
}

export interface PriceEstimate {
  gross: number
  net?: number
  tier: string
  unit: ShortTermUnit
  quantity: number
  unitRateGross: number
}

/**
 * Podgląd ceny (§1.1) — źródłem prawdy jest koszyk (rentalWindow.pricing).
 * D = czas zaokrąglony w górę do unitMinutes; każdy próg: rate × max(ceil(D / minuty_jednostki), threshold);
 * higherUnitCap → minimum kandydatów, inaczej kandydat największego progu ≤ D.
 */
export const estimatePrice = (shortTerm: ShortTermConfig, minutes: number): PriceEstimate | undefined => {
  const grid = (shortTerm.grid || []).filter(t => UNIT_MINUTES[t.unit])
  if (grid.length === 0 || !(minutes > 0)) return
  const D = roundUp(minutes, shortTerm.unitMinutes || 1)

  const candidates = grid.map(tier => {
    const quantity = Math.max(Math.ceil(D / UNIT_MINUTES[tier.unit]), Number(tier.threshold) || 1)
    const unitRateGross = Number(tier.rateGross ?? tier.rate)
    return {
      tier: tier.tier,
      unit: tier.unit,
      quantity,
      unitRateGross,
      gross: round2(unitRateGross * quantity),
      net: tier.rateNet !== undefined ? round2(Number(tier.rateNet) * quantity) : undefined,
      tierMinutes: Number(tier.minutes) || UNIT_MINUTES[tier.unit] * (Number(tier.threshold) || 1)
    }
  })

  let best = candidates[0]
  if (shortTerm.higherUnitCap !== false) {
    for (const c of candidates) if (c.gross < best.gross) best = c
  } else {
    const fitting = candidates.filter(c => c.tierMinutes <= D).sort((a, b) => b.tierMinutes - a.tierMinutes)
    best = fitting[0] || candidates.sort((a, b) => a.tierMinutes - b.tierMinutes)[0]
  }

  const { tierMinutes, ...estimate } = best
  return estimate
}

const round2 = (value: number) => Math.round(value * 100) / 100

// ---------------------------------------------------------------------------------------------

/** 90 -> "1 h 30 min", 1440 -> "1 doba", 2880 -> "2 doby" */
export const durationLabel = (minutes: number, lang = 'pl') => {
  if (!(minutes > 0)) return '-'
  const days = Math.floor(minutes / DAY_MINUTES)
  const hours = Math.floor((minutes % DAY_MINUTES) / 60)
  const mins = minutes % 60
  const parts: string[] = []
  if (days) parts.push(`${days} ${lang === 'pl' ? (days === 1 ? 'dzień' : 'dni') : (days === 1 ? 'day' : 'days')}`)
  if (hours) parts.push(`${hours} h`)
  if (mins) parts.push(`${mins} min`)
  return parts.join(' ')
}

/** etykieta dnia czasu ściennego, np. "pt, 10.10" */
export const wallDayLabel = (minutes: number, lang = 'pl', options: Intl.DateTimeFormatOptions = { weekday: 'short', day: '2-digit', month: '2-digit' }) =>
  new Date(minutes * 60000).toLocaleDateString(lang, { ...options, timeZone: 'UTC' })

/** "10.10.2026 10:00 → 13:00" albo "10.10.2026 10:00 → 11.10.2026 09:00" */
export const formatWindowRange = (startDateTime?: string, durationMinutes?: number, endDateTime?: string, lang = 'pl') => {
  const start = parseWall(startDateTime)
  if (start === undefined) return '-'
  const end = parseWall(endDateTime) ?? (durationMinutes ? start + Number(durationMinutes) : undefined)
  const date = (m: number) => wallDayLabel(m, lang, { day: '2-digit', month: '2-digit', year: 'numeric' })
  const startLabel = `${date(start)} ${wallTime(start)}`
  if (end === undefined) return `${startLabel} → ∞`
  return `${startLabel} → ${wallDay(end) === wallDay(start) ? '' : `${date(end)} `}${wallTime(end)}`
}

/** długość okna pozycji koszyka (durationMinutes albo różnica do endDateTime) */
export const getItemDuration = (item: { startDateTime?: string, durationMinutes?: number, endDateTime?: string }) => {
  const start = parseWall(item.startDateTime)
  const end = parseWall(item.endDateTime)
  if (start !== undefined && end !== undefined) return end - start
  return item.durationMinutes ? Number(item.durationMinutes) : undefined
}

/** najdłuższy czas ≤ żądanego, który mieści się przed kolejnym zajętym oknem (nie krótszy niż minimum polityki) */
export const fitDuration = (shortTerm: ShortTermConfig, busy: BusyInterval[], start: number, wanted: number) => {
  const unit = Math.max(1, Number(shortTerm.unitMinutes) || 30)
  const minMinutes = Math.max(Number(shortTerm.minMinutes) || unit, unit)
  const maxMinutes = shortTerm.maxMinutes ? Number(shortTerm.maxMinutes) : Infinity
  const limit = roundDown(freeUntil(busy, start) - start, unit)
  return Math.max(minMinutes, Math.min(wanted, maxMinutes, limit))
}

/** wolne starty w [from, until) co `step` minut, w których mieści się minimalny najem */
export const getFreeStarts = (shortTerm: ShortTermConfig, busy: BusyInterval[], from: number, until: number) => {
  const step = getSlotStep(shortTerm)
  const minMinutes = Math.max(Number(shortTerm.minMinutes) || Number(shortTerm.unitMinutes) || 30, 1)
  const result: number[] = []
  for (let s = roundUp(from, step); s < until; s += step) {
    if (isWindowFree(busy, s, s + minMinutes)) result.push(s)
  }
  return result
}
